///
/// EarthBrowser
///
/// Created by Matt Giger on 8/29/2024
/// Copyright (c) 2024 Geodata Labs
///

type QueueFunction<T> = () => Promise<T> | T
type Resolver<T> = (value: T) => void
type Rejecter = (reason?: unknown) => void
type Priority = 'critical' | 'high' | 'medium' | 'low' | 'veryLow'

const PriorityRank: Record<Priority, number> = {
	critical: 0,
	high: 1,
	medium: 2,
	low: 3,
	veryLow: 4
}

type QueueItem<T> = {
	func: QueueFunction<T>
	resolve: Resolver<T>
	reject: Rejecter
	priority: Priority
}

/**
 * AsyncQueue class for managing asynchronous operations in a queue.
 * @template T The type of value returned by the queued functions.
 */
export class AsyncQueue<T> {
	/** Array to store queued items */
	private queue: QueueItem<T>[] = []
	private isProcessing = false
	private statusCallback: (status: "idle" | "busy") => void = () => {}

	/**
	 * Sets the status callback for the queue.
	 * @param callback The callback function to update the status of the queue.
	 */
	public setStatusCallback(callback: (status: "idle" | "busy") => void): void {
		this.statusCallback = callback
	}

	/**
	 * Adds a function to the queue and returns a promise that resolves with the function's result.
	 * @param item The function to be added to the queue.
	 * @returns A promise that resolves with the result of the queued function.
	 */
	public async add(item: QueueFunction<T>, priority: Priority = "medium"): Promise<T> {
		return new Promise<T>((resolve, reject) => {
			this.enqueue(item, resolve, reject, priority)
		})
	}

	/**
	 * Clears the queue.
	 */
	public clear(): void {
		this.queue = []
	}

	/**
	 * Adds an item to the queue.
	 * @param func The function to be queued.
	 * @param resolve The resolver function for the promise.
	 * @param reject The rejector function for the promise.
	 */
	private enqueue(func: QueueFunction<T>, resolve: Resolver<T>, reject: Rejecter, priority: Priority): void {
		this.queue.push({ func, resolve, reject, priority })
		this.queue.sort((a, b) =>
			PriorityRank[a.priority] - PriorityRank[b.priority]	// ascending order
		)
		void this.processQueue()
	}

	/**
	 * Processes the queue of items.
	 */
	private async processQueue(): Promise<void> {
		if (!this.isProcessing) {
			this.isProcessing = true
			this.statusCallback("busy")

			while (this.queue.length > 0) {
				const next = this.queue.shift()
				if (next) {
					try {
						const result = await next.func()
						next.resolve(result)
					} catch(err) {
						next.reject(err)
					}
				}
			}

			this.isProcessing = false
			this.statusCallback("idle")
		}
	}
}
